import queryString from 'query-string';
import { History } from 'history';
import { getHistorySearch } from './navigation-url';

/**
 * Get parsed query parameters from history
 */
export const getQueryParams = (history: History) => {
  const search = getHistorySearch(history);
  return queryString.parse(decodeURI(search));
};

/**
 * Normalize a section ID by trimming whitespace and replacing / with - for URL safety
 * Also decodes URL-encoded characters and removes .app suffix and invalid characters
 */
export const normalizeSectionId = (sectionId: string): string => {
  if (!sectionId) return '';
  // Decode URL-encoded characters (e.g., %20 -> space)
  let normalized = decodeURIComponent(sectionId);
  // Trim whitespace at the start to prevent leading hyphens
  normalized = normalized.trim();
  // Remove .app suffix if present
  normalized = normalized.replace(/\.app$/, '');
  // Remove special characters and symbols (®, ™, ©, etc.) that can affect crawlable links
  // Keep only alphanumeric, spaces, hyphens, underscores, and basic punctuation
  normalized = normalized.replace(/[^\w\s\-_]/g, '');
  // Replace / with - for URL safety
  normalized = normalized.replace(/\//g, '-');
  // Replace dots with - for URL safety
  normalized = normalized.replace(/\./g, '-');
  // Remove spaces and other invalid URL characters, replace with -
  normalized = normalized.replace(/[\s]+/g, '-');
  // Remove leading and trailing hyphens that may result from space replacement
  normalized = normalized.replace(/^-+|-+$/g, '');
  return normalized;
};

/**
 * Get section from query params, normalized
 */
export const getSectionFromQuery = (
  query: queryString.ParsedQuery<string>
): string => {
  return normalizeSectionId((query.section as string) || '');
};

/**
 * Get search term from query params
 */
export const getSearchTermFromQuery = (
  query: queryString.ParsedQuery<string>
): string => {
  return (query.search as string) || '';
};

/**
 * Get search page from query params
 */
export const getSearchPageFromQuery = (
  query: queryString.ParsedQuery<string>
): number => {
  const pageStr = (query.page as string) || '1';
  return parseInt(pageStr, 10) || 1;
};

/**
 * Get guide mode from query params
 */
export const getGuideModeFromQuery = (
  query: queryString.ParsedQuery<string>,
  defaultValue: string = ''
): string => {
  return (query['guide-mode'] as string) || defaultValue;
};

/**
 * Check if we're on the guide route (/:modelYear/:modelId or /:modelYear/:modelId/:section)
 */
export const isGuideRoute = (pathname: string): boolean => {
  const pathMatch = pathname.match(/^\/([^/]+)\/([^/]+)(?:\/([^/]+))?$/);
  return pathMatch !== null && pathMatch[1] !== '' && pathMatch[2] !== '';
};

/**
 * Get modelYear, modelId, and section from path params
 */
export const getModelPathParams = (
  pathname: string
): {
  modelYear: string;
  modelId: string;
  section: string;
} => {
  const pathMatch = pathname.match(/^\/([^/]+)\/([^/]+)(?:\/([^/]+))?$/);
  if (pathMatch) {
    return {
      modelYear: pathMatch[1] || '',
      modelId: pathMatch[2] || '',
      section: pathMatch[3] || '',
    };
  }
  return { modelYear: '', modelId: '', section: '' };
};

/**
 * Get section from path params (when on guide route)
 */
export const getSectionFromPath = (pathname: string): string => {
  const pathParams = getModelPathParams(pathname);
  return normalizeSectionId(pathParams.section || '');
};

/**
 * Get model year from history (path params on guide route, query params otherwise)
 */
export const getModelYearFromHistory = (history: History): string => {
  const pathParams = getModelPathParams(history.location.pathname);
  if (isGuideRoute(history.location.pathname)) {
    return pathParams.modelYear;
  }
  const query = getQueryParams(history);
  return (query['model-year'] as string) || '';
};

/**
 * Get model ID from history (path params on guide route, query params otherwise)
 */
export const getModelIdFromHistory = (history: History): string => {
  const pathParams = getModelPathParams(history.location.pathname);
  if (isGuideRoute(history.location.pathname)) {
    return pathParams.modelId;
  }
  const query = getQueryParams(history);
  return (query.model as string) || '';
};

/**
 * Get default guide mode based on route and section
 * - If inside guide route and there's a section in path -> 'content'
 * - If inside guide route and no section in path -> 'navigation'
 * - If not inside guide route -> 'model-selector' (empty string)
 */
export const getDefaultGuideMode = (
  pathname: string,
  currentSection?: string
): string => {
  if (!isGuideRoute(pathname)) {
    return '';
  }

  // Check section in path first, then fall back to currentSection parameter
  const sectionInPath = getSectionFromPath(pathname);
  const section = sectionInPath || currentSection || '';

  return section ? 'content' : 'navigation';
};

/**
 * Normalize query object to a Record with string values, handling arrays
 * Converts arrays to their first element, and null/undefined to undefined
 */
export const normalizeQueryToParams = (
  query: queryString.ParsedQuery<string>
): Record<string, string | undefined> => {
  return Object.fromEntries(
    Object.entries(query).map(([k, v]) => [
      k,
      Array.isArray(v) ? v[0] || undefined : (v as string) || undefined,
    ])
  );
};

/**
 * Convert params object to search string, filtering out undefined values
 */
export const paramsToSearchString = (
  params: Record<string, string | undefined>
): string => {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_, v]) => v !== undefined)
  );
  return Object.keys(filteredParams).length > 0
    ? queryString.stringify(filteredParams)
    : '';
};
